"use client"

import { ProductsSearchDataResponse } from "@repo/api-shared"
import { useEffect, useMemo, useState, useCallback } from "react"
import { FiltersBlock } from "./_components/components/filters-block"
import { ProductsList } from "./_components/components/products-list"
import { z } from "zod"

const STORAGE_KEYS = {
    FILTERS_ACCORDIONS: "filters_accordions_state",
    SEARCH: "search_query",
    PRICE_RANGE: "price_range",
    CATEGORIES: "selected_categories",
    SIZES: "selected_sizes",
    TAGS: "selected_tags",
} as const

const defaultFiltersAccordionsState = {
    price: true,
    categories: true,
    sizes: true,
    tags: false,
}

function calculateFilterCounts(
    products: ProductsSearchDataResponse["products"],
    filters: ProductsSearchDataResponse["filters"],
    selectedCategories: string[],
    selectedSizes: string[],
    selectedTags: string[],
    selectedPriceRange: [number, number],
) {
    const initialProductIds = new Set(Object.keys(products))

    // Helper function to get filtered products for a filter type
    const getFilteredProducts = (
        filterType: "categories" | "sizes" | "tags",
        selectedFilters: string[],
    ) => {
        if (selectedFilters.length === 0) return initialProductIds

        const matchingProductIds = new Set<string>()
        selectedFilters.forEach((filterId) => {
            const productsForFilter =
                filters[filterType][filterId]?.productIds || []
            productsForFilter.forEach((productId) => {
                if (initialProductIds.has(productId)) {
                    matchingProductIds.add(productId)
                }
            })
        })
        return matchingProductIds
    }

    // Get base filtered products for each type
    const categoryFiltered = getFilteredProducts(
        "categories",
        selectedCategories,
    )
    const sizeFiltered = getFilteredProducts("sizes", selectedSizes)
    const tagFiltered = getFilteredProducts("tags", selectedTags)

    // Calculate available products after price filter
    const priceFiltered = new Set(
        Array.from(initialProductIds).filter((id) => {
            const product = products[id]
            return product?.prices.some(
                (price) =>
                    price.calculated_amount >= selectedPriceRange[0] &&
                    price.calculated_amount <= selectedPriceRange[1],
            )
        }),
    )

    // Calculate dynamic counts for each filter type
    const dynamicFilters = {
        categories: { ...filters.categories },
        sizes: { ...filters.sizes },
        tags: { ...filters.tags },
        priceRange: filters.priceRange,
    }

    // For categories, apply size and tag filters
    Object.entries(filters.categories).forEach(([categoryId, category]) => {
        const availableAfterOtherFilters = new Set(
            Array.from(category.productIds)
                .filter((id) => sizeFiltered.has(id))
                .filter((id) => tagFiltered.has(id))
                .filter((id) => priceFiltered.has(id)),
        )
        dynamicFilters.categories[categoryId] = {
            productIds: category.productIds,
            count: availableAfterOtherFilters.size,
        }
    })

    // For sizes, apply category and tag filters
    Object.entries(filters.sizes).forEach(([sizeId, size]) => {
        const availableAfterOtherFilters = new Set(
            Array.from(size.productIds)
                .filter((id) => categoryFiltered.has(id))
                .filter((id) => tagFiltered.has(id))
                .filter((id) => priceFiltered.has(id)),
        )
        dynamicFilters.sizes[sizeId] = {
            productIds: size.productIds,
            count: availableAfterOtherFilters.size,
        }
    })

    // For tags, apply category and size filters
    Object.entries(filters.tags).forEach(([tagId, tag]) => {
        const availableAfterOtherFilters = new Set(
            Array.from(tag.productIds)
                .filter((id) => categoryFiltered.has(id))
                .filter((id) => sizeFiltered.has(id))
                .filter((id) => priceFiltered.has(id)),
        )
        dynamicFilters.tags[tagId] = {
            productIds: tag.productIds,
            count: availableAfterOtherFilters.size,
        }
    })

    return dynamicFilters
}

export function CatalogPageClient({
    products,
    filters,
}: {
    products: ProductsSearchDataResponse["products"] | null | undefined
    filters: ProductsSearchDataResponse["filters"] | null | undefined
}) {
    const [isClient, setIsClient] = useState(false)
    useEffect(() => {
        setIsClient(true)
    }, [])

    // Filters accordions state
    const [filtersAccordionsState, setFiltersAccordionsState] = useState(
        defaultFiltersAccordionsState,
    )

    useEffect(() => {
        if (isClient) {
            const saved = sessionStorage.getItem(
                STORAGE_KEYS.FILTERS_ACCORDIONS,
            )
            setFiltersAccordionsState(
                saved ? JSON.parse(saved) : defaultFiltersAccordionsState,
            )
        }
    }, [isClient])

    useEffect(() => {
        if (isClient) {
            sessionStorage.setItem(
                STORAGE_KEYS.FILTERS_ACCORDIONS,
                JSON.stringify(filtersAccordionsState),
            )
        }
    }, [filtersAccordionsState, isClient])

    const toggleSection = (
        sectionId: keyof typeof defaultFiltersAccordionsState,
    ) => {
        setFiltersAccordionsState(
            (prev: typeof defaultFiltersAccordionsState) => ({
                ...prev,
                [sectionId]: !prev[sectionId],
            }),
        )
    }

    // Search
    const [searchQuery, setSearchQuery] = useState("")

    useEffect(() => {
        if (isClient) {
            setSearchQuery(sessionStorage.getItem(STORAGE_KEYS.SEARCH) || "")
        }
    }, [isClient])

    useEffect(() => {
        if (isClient) {
            sessionStorage.setItem(STORAGE_KEYS.SEARCH, searchQuery)
        }
    }, [searchQuery, isClient])

    const minPrice = filters?.priceRange.min || 10_000
    const maxPrice = filters?.priceRange.max
        ? Math.ceil(filters?.priceRange.max / 1000) * 1000
        : 1_000_000

    // Price
    const defaultPriceRange: [number, number] = [minPrice, maxPrice]

    const [selectedPriceRange, setSelectedPriceRange] =
        useState<[number, number]>(defaultPriceRange)

    useEffect(() => {
        if (isClient) {
            const saved = sessionStorage.getItem(STORAGE_KEYS.PRICE_RANGE)
            const savedPriceRange = saved && JSON.parse(saved)
            let initSelectedMinPrice = 0
            let initSelectedMaxPrice = 1_000_000

            const parseResult = z
                .tuple([z.number(), z.number()])
                .safeParse(savedPriceRange)

            if (parseResult.success && parseResult.data.length === 2) {
                if (parseResult.data[0] !== undefined) {
                    initSelectedMinPrice = parseResult.data[0]
                }
                if (parseResult.data[1] !== undefined) {
                    initSelectedMaxPrice = parseResult.data[1]
                }
            }

            if (
                typeof minPrice === "number" &&
                initSelectedMinPrice < minPrice
            ) {
                initSelectedMinPrice = minPrice
            }
            if (
                typeof maxPrice === "number" &&
                initSelectedMaxPrice > maxPrice
            ) {
                initSelectedMaxPrice = maxPrice
            }

            setSelectedPriceRange([initSelectedMinPrice, initSelectedMaxPrice])
        }
    }, [isClient, minPrice, maxPrice])

    useEffect(() => {
        if (isClient) {
            sessionStorage.setItem(
                STORAGE_KEYS.PRICE_RANGE,
                JSON.stringify(selectedPriceRange),
            )
        }
    }, [selectedPriceRange, isClient])

    const handleMinPriceChange = useCallback(
        (minPrice: number) => {
            setSelectedPriceRange((prev) => [minPrice, prev[1]])
        },
        [setSelectedPriceRange],
    )
    const handleMaxPriceChange = useCallback(
        (maxPrice: number) => {
            setSelectedPriceRange((prev) => [prev[0], maxPrice])
        },
        [setSelectedPriceRange],
    )

    // Categories
    const [selectedCategories, setSelectedCategories] = useState<string[]>([])

    useEffect(() => {
        if (isClient) {
            const saved = sessionStorage.getItem(STORAGE_KEYS.CATEGORIES)
            setSelectedCategories(saved ? JSON.parse(saved) : [])
        }
    }, [isClient])

    useEffect(() => {
        if (isClient) {
            sessionStorage.setItem(
                STORAGE_KEYS.CATEGORIES,
                JSON.stringify(selectedCategories),
            )
        }
    }, [selectedCategories, isClient])

    const handleCategoryToggle = (categoryId: string) => {
        setSelectedCategories((prev) =>
            prev.includes(categoryId)
                ? prev.filter((id) => id !== categoryId)
                : [...prev, categoryId],
        )
    }

    // Sizes
    const [selectedSizes, setSelectedSizes] = useState<string[]>([])

    useEffect(() => {
        if (isClient) {
            const saved = sessionStorage.getItem(STORAGE_KEYS.SIZES)
            setSelectedSizes(saved ? JSON.parse(saved) : [])
        }
    }, [isClient])

    useEffect(() => {
        if (isClient) {
            sessionStorage.setItem(
                STORAGE_KEYS.SIZES,
                JSON.stringify(selectedSizes),
            )
        }
    }, [selectedSizes, isClient])

    const handleSizeToggle = (sizeId: string) => {
        setSelectedSizes((prev) =>
            prev.includes(sizeId)
                ? prev.filter((id) => id !== sizeId)
                : [...prev, sizeId],
        )
    }

    // Tags
    const [selectedTags, setSelectedTags] = useState<string[]>([])

    useEffect(() => {
        if (isClient) {
            const saved = sessionStorage.getItem(STORAGE_KEYS.TAGS)
            setSelectedTags(saved ? JSON.parse(saved) : [])
        }
    }, [isClient])

    useEffect(() => {
        if (isClient) {
            sessionStorage.setItem(
                STORAGE_KEYS.TAGS,
                JSON.stringify(selectedTags),
            )
        }
    }, [selectedTags, isClient])

    const handleTagToggle = (tagId: string) => {
        setSelectedTags((prev) =>
            prev.includes(tagId)
                ? prev.filter((id) => id !== tagId)
                : [...prev, tagId],
        )
    }

    const resetFilters = () => {
        setSearchQuery("")
        setSelectedPriceRange(defaultPriceRange)
        setSelectedCategories([])
        setSelectedSizes([])
        setSelectedTags([])

        // Clear sessionStorage
        Object.values(STORAGE_KEYS).forEach((key) =>
            sessionStorage.removeItem(key),
        )
    }

    const filteredProducts: Array<
        ProductsSearchDataResponse["products"][string]
    > = useMemo(() => {
        if (!isClient || !products || !filters) {
            return products ? Object.values(products) : []
        }

        const initialProductIds = new Set(Object.keys(products))

        // Get filtered products for each filter type
        const productsIdsFilteredByCategories = filterProducts({
            initialProductIds,
            selectedFilters: selectedCategories,
            filterProductIdsMap: filters.categories,
        })

        const productsIdsFilteredBySizes = filterProducts({
            initialProductIds,
            selectedFilters: selectedSizes,
            filterProductIdsMap: filters.sizes,
        })

        const productsIdsFilteredByTags = filterProducts({
            initialProductIds,
            selectedFilters: selectedTags,
            filterProductIdsMap: filters.tags,
        })

        // Perform intersection between different filter types
        let finalProductIds = new Set(initialProductIds)

        // Only apply category filter if categories are selected
        if (selectedCategories.length > 0) {
            finalProductIds = new Set(
                [...finalProductIds].filter((id) =>
                    productsIdsFilteredByCategories.has(id),
                ),
            )
        }

        // Only apply size filter if sizes are selected
        if (selectedSizes.length > 0) {
            finalProductIds = new Set(
                [...finalProductIds].filter((id) =>
                    productsIdsFilteredBySizes.has(id),
                ),
            )
        }

        // Only apply tag filter if tags are selected
        if (selectedTags.length > 0) {
            finalProductIds = new Set(
                [...finalProductIds].filter((id) =>
                    productsIdsFilteredByTags.has(id),
                ),
            )
        }

        // Convert filtered IDs to products
        const filteredProducts = Array.from(finalProductIds)
            .map((id) => products[id])
            .filter(
                (product): product is NonNullable<typeof product> =>
                    product !== undefined,
            )

        // Apply price filter
        const filteredProductsByPrice = filteredProducts.filter((product) => {
            return product.prices.some(
                (price) =>
                    price.calculated_amount >= selectedPriceRange[0] &&
                    price.calculated_amount <= selectedPriceRange[1],
            )
        })

        // Apply search filter
        const filteredProductsBySearch = searchQuery
            ? filteredProductsByPrice.filter((product) => {
                  return product.title
                      .toLowerCase()
                      .includes(searchQuery.toLowerCase())
              })
            : filteredProductsByPrice

        return filteredProductsBySearch
    }, [
        isClient,
        filters,
        products,
        searchQuery,
        selectedPriceRange,
        selectedCategories,
        selectedSizes,
        selectedTags,
    ])

    const [loading, setLoading] = useState(false)

    const fakeLoading = () => {
        setLoading(true)
        setTimeout(() => {
            setLoading(false)
        }, 300)
    }

    // Calculate dynamic filter counts
    const dynamicFilters = useMemo(() => {
        if (!isClient || !products || !filters) return filters
        return calculateFilterCounts(
            products,
            filters,
            selectedCategories,
            selectedSizes,
            selectedTags,
            selectedPriceRange,
        )
    }, [
        isClient,
        products,
        filters,
        selectedCategories,
        selectedSizes,
        selectedTags,
        selectedPriceRange,
    ])

    return (
        <main className="container mx-auto px-4 py-8">
            <div className="flex flex-col gap-6 md:flex-row">
                <div className="w-full md:w-1/4">
                    <FiltersBlock
                        filters={dynamicFilters}
                        filtersAccordionsState={filtersAccordionsState}
                        toggleSection={toggleSection}
                        searchQuery={searchQuery}
                        onSearchQueryChange={setSearchQuery}
                        selectedPriceRange={selectedPriceRange}
                        minPrice={minPrice}
                        maxPrice={maxPrice}
                        onMinPriceChange={handleMinPriceChange}
                        onMaxPriceChange={handleMaxPriceChange}
                        selectedCategories={selectedCategories}
                        onCategoryToggle={handleCategoryToggle}
                        selectedSizes={selectedSizes}
                        onSizeToggle={handleSizeToggle}
                        selectedTags={selectedTags}
                        onTagToggle={handleTagToggle}
                        onSubmit={fakeLoading}
                        resetFilters={resetFilters}
                    />
                </div>

                <div className="w-full md:w-3/4">
                    <ProductsList
                        products={filteredProducts}
                        selectedPriceRange={selectedPriceRange}
                        loading={loading}
                    />
                </div>
            </div>
        </main>
    )
}

function filterProducts({
    initialProductIds,
    selectedFilters,
    filterProductIdsMap,
}: {
    initialProductIds: Set<string>
    selectedFilters: string[]
    filterProductIdsMap: Record<string, { productIds: string[] }>
}) {
    // If no filters selected, return all initial products
    if (selectedFilters.length === 0) {
        return initialProductIds
    }

    // Create a set to store products that match ANY of the selected filters (union)
    const matchingProductIds = new Set<string>()

    // Add all product IDs that match any of the selected filters
    selectedFilters.forEach((filterId) => {
        const productsForFilter =
            filterProductIdsMap[filterId]?.productIds || []
        productsForFilter.forEach((productId) => {
            if (initialProductIds.has(productId)) {
                matchingProductIds.add(productId)
            }
        })
    })

    return matchingProductIds
}
